import { AbstractControl, ValidatorFn, ValidationErrors } from '@angular/forms';

/**
 * Creates a validator that ensures a date is greater than the specified value
 * @description Supports 'now' keyword for current date/time validation and ISO date strings
 * @param compareValue The value to compare against ('now' or ISO date string)
 * @returns ValidatorFn that validates the date is greater than the compare value
 */
export function createDateGreaterThanValidator(compareValue: string | number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        if (!control.value) {
            return null; // Don't validate empty values (use required validator for that)
        }

        const inputDate = new Date(control.value);

        // Check if input date is valid
        if (isNaN(inputDate.getTime())) {
            return { 'invalid-date': { value: control.value } };
        }

        let compareDate: Date;

        if (compareValue === 'now') {
            compareDate = new Date();
        } else {
            compareDate = new Date(compareValue);

            // Check if compare date is valid
            if (isNaN(compareDate.getTime())) {
                console.warn(`Invalid compare date value: ${compareValue}`);
                return null;
            }
        }

        // For date-only comparison (ignoring time), reset time components
        // When comparing with 'now', we want to allow today's date, so compare at start of day
        if (compareValue === 'now') {
            // Set both dates to start of day for comparison
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            inputDate.setHours(0, 0, 0, 0);
            compareDate = today;
        } else if (typeof compareValue === 'string' && !compareValue.includes('T')) {
            inputDate.setHours(0, 0, 0, 0);
            compareDate.setHours(0, 0, 0, 0);
        }

        // Create user-friendly display value for error message
        const displayValue =
            compareValue === 'now'
                ? compareDate.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })
                : compareValue;

        return inputDate >= compareDate
            ? null
            : {
                  'date-not-greater': {
                      value: control.value,
                      compareValue: displayValue,
                      actual: inputDate.toISOString(),
                      expected: compareDate.toISOString()
                  }
              };
    };
}
